<script lang="ts">
  import { onMount } from 'svelte';
  import { Camera, Upload, X, User as UserIcon, Mail, Key, Save } from 'lucide-svelte';
  import { user, authActions } from '../../stores/auth';
  import { takePhoto, pickFromGallery, uploadAvatarToStorage, deleteAvatarFromStorage } from '../../lib/avatar-upload';
  import Button from '../ui/button.svelte';
  import Card from '../ui/card.svelte';
  import { hapticFeedback } from '../../lib/animations';
  import { _ } from 'svelte-i18n';

  let {
    open = false,
    onClose,
  }: {
    open: boolean;
    onClose: () => void;
  } = $props();

  // Profile data - initialize empty, will be filled from $user
  let fullName = $state('');
  let displayName = $state('');
  let avatarUrl = $state('');
  let email = $derived($user?.email || '');

  // Password change (only for email users)
  let currentPassword = $state('');
  let newPassword = $state('');
  let confirmPassword = $state('');

  // UI state
  let isUploading = $state(false);
  let isSaving = $state(false);
  let errorMessage = $state<string | null>(null);
  let successMessage = $state<string | null>(null);

  // Detect auth method
  let authMethod = $derived(authActions.getAuthMethod($user));
  let isEmailUser = $derived(authMethod === 'email');

  // Active tab
  let activeTab = $state<'profile' | 'security'>('profile');

  let isPasswordValid = $derived(
    activeTab === 'security' &&
    newPassword.length >= 6 &&
    newPassword === confirmPassword
  );

  let passwordStrength = $derived(getPasswordStrength(newPassword));

  // Validation
  let isFormValid = $derived(
    fullName.trim().length > 0 &&
    (activeTab === 'profile' || isPasswordValid)
  );

  function getPasswordStrength(password: string): 'weak' | 'medium' | 'strong' {
    if (password.length < 6) return 'weak';
    if (password.length < 10) return 'medium';
    if (/[A-Z]/.test(password) && /[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)) {
      return 'strong';
    }
    return 'medium';
  }

  async function handleTakePhoto() {
    try {
      await hapticFeedback('light');
      errorMessage = null;
      const dataUrl = await takePhoto();

      if (dataUrl && $user) {
        isUploading = true;
        const { url, error } = await uploadAvatarToStorage(dataUrl, $user.id);

        if (error) {
          errorMessage = error;
        } else if (url) {
          // Delete old avatar if exists
          if (avatarUrl) {
            await deleteAvatarFromStorage(avatarUrl);
          }
          avatarUrl = url;
          successMessage = $_('profile.avatar.uploaded');
        }
      }
    } catch (error) {
      errorMessage = $_('profile.avatar.photo_error');
      console.error('Photo error:', error);
    } finally {
      isUploading = false;
    }
  }

  async function handlePickFromGallery() {
    try {
      await hapticFeedback('light');
      errorMessage = null;
      const dataUrl = await pickFromGallery();

      if (dataUrl && $user) {
        isUploading = true;
        const { url, error } = await uploadAvatarToStorage(dataUrl, $user.id);

        if (error) {
          errorMessage = error;
        } else if (url) {
          // Delete old avatar if exists
          if (avatarUrl) {
            await deleteAvatarFromStorage(avatarUrl);
          }
          avatarUrl = url;
          successMessage = $_('profile.avatar.uploaded');
        }
      }
    } catch (error) {
      errorMessage = $_('profile.avatar.gallery_error');
      console.error('Gallery error:', error);
    } finally {
      isUploading = false;
    }
  }

  async function handleRemoveAvatar() {
    try {
      await hapticFeedback('medium');
      if (avatarUrl) {
        await deleteAvatarFromStorage(avatarUrl);
        avatarUrl = '';
        successMessage = $_('profile.avatar.removed');
      }
    } catch (error) {
      errorMessage = $_('profile.avatar.remove_error');
      console.error('Remove avatar error:', error);
    }
  }

  async function handleSaveProfile() {
    try {
      isSaving = true;
      errorMessage = null;
      successMessage = null;

      await hapticFeedback('medium');

      const updates: any = {
        full_name: fullName.trim(),
        nickname: displayName.trim() || fullName.trim(),
      };

      if (avatarUrl) {
        updates.avatar_url = avatarUrl;
      }

      const result = await authActions.updateProfile(updates);

      if (result.success) {
        successMessage = $_('profile.save.profile_saved');
        await hapticFeedback('heavy');
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        errorMessage = result.error || $_('profile.save.save_error');
        await hapticFeedback('medium');
      }
    } catch (error) {
      errorMessage = $_('profile.save.save_error');
      await hapticFeedback('medium');
      console.error('Save profile error:', error);
    } finally {
      isSaving = false;
    }
  }

  async function handleChangePassword() {
    try {
      if (!isPasswordValid) {
        errorMessage = $_('profile.security.password_invalid');
        return;
      }

      isSaving = true;
      errorMessage = null;
      successMessage = null;

      await hapticFeedback('medium');

      const result = await authActions.updatePassword(newPassword);

      if (result.success) {
        successMessage = $_('profile.security.password_changed');
        await hapticFeedback('heavy');
        currentPassword = '';
        newPassword = '';
        confirmPassword = '';
      } else {
        errorMessage = result.error || $_('profile.security.password_error');
        await hapticFeedback('medium');
      }
    } catch (error) {
      errorMessage = $_('profile.security.password_error');
      await hapticFeedback('medium');
      console.error('Change password error:', error);
    } finally {
      isSaving = false;
    }
  }

  function handleCancel() {
    hapticFeedback('light');
    onClose();
  }

  onMount(() => {
    // Initialize profile data from user store
    if ($user) {
      fullName = $user.user_metadata?.full_name || '';
      displayName = $user.user_metadata?.nickname || '';
      avatarUrl = $user.user_metadata?.avatar_url || '';
    }

    if (open) {
      const previousOverflow = document.body.style.overflow;
      const previousPadding = document.body.style.paddingRight;
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

      document.body.style.overflow = 'hidden';
      if (scrollBarWidth > 0) {
        document.body.style.paddingRight = `${scrollBarWidth}px`;
      }

      return () => {
        document.body.style.overflow = previousOverflow;
        document.body.style.paddingRight = previousPadding;
      };
    }
  });
</script>

{#if open}
  <div
    role="dialog"
    aria-modal="true"
    tabindex="0"
    class="fixed inset-0 z-[96] flex flex-col bg-background"
    onclick={(e) => { if (e.target === e.currentTarget) handleCancel(); }}
    onkeydown={(e) => { if (e.key === 'Escape') handleCancel(); }}
  >
    <!-- Header -->
    <div class="flex items-center justify-between p-6 border-b border-border">
      <h2 class="text-xl font-bold text-foreground">{$_('profile.edit_title')}</h2>
      <button onclick={handleCancel} class="btn btn-close" aria-label={$_('aria.close')}>
        <X class="w-5 h-5" />
      </button>
    </div>

    <!-- Tabs -->
    <div class="tab-switcher mx-6 mt-4">
      <button
        onclick={() => activeTab = 'profile'}
        class="tab-item {activeTab === 'profile' ? 'active' : ''}"
      >
        {$_('profile.tabs.profile')}
      </button>
      {#if isEmailUser}
        <button
          onclick={() => activeTab = 'security'}
          class="tab-item {activeTab === 'security' ? 'active' : ''}"
        >
          {$_('profile.tabs.security')}
        </button>
      {/if}
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto px-6 pb-24">
      <div class="mx-auto max-w-md py-6 space-y-4">
        {#if errorMessage}
          <div class="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
            {errorMessage}
          </div>
        {/if}

        {#if successMessage}
          <div class="mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg text-primary text-sm">
            {successMessage}
          </div>
        {/if}

        {#if activeTab === 'profile'}
          <!-- Profile Tab -->
          <Card class="widget-card tone-header">
            <div class="space-y-4">
              <!-- Avatar Section -->
              <div class="flex flex-col items-center gap-3 pb-4 border-b border-foreground/10">
                <div class="relative">
                  {#if avatarUrl}
                    <img src={avatarUrl} alt="Avatar" class="w-24 h-24 rounded-full object-cover" />
                  {:else}
                    <div class="w-24 h-24 rounded-full bg-foreground/10 flex items-center justify-center">
                      <UserIcon class="w-12 h-12 text-foreground/50" />
                    </div>
                  {/if}
                  {#if isUploading}
                    <div class="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    </div>
                  {/if}
                </div>

                <div class="flex flex-wrap items-center justify-center gap-2">
                  <Button onclick={handleTakePhoto} variant="outline" size="sm" disabled={isUploading}>
                    <Camera class="w-4 h-4 mr-2" />
                    {$_('profile.avatar.take_photo')}
                  </Button>
                  <Button onclick={handlePickFromGallery} variant="outline" size="sm" disabled={isUploading}>
                    <Upload class="w-4 h-4 mr-2" />
                    {$_('profile.avatar.from_gallery')}
                  </Button>
                  {#if avatarUrl}
                    <Button onclick={handleRemoveAvatar} variant="outline" size="sm" disabled={isUploading}>
                      <X class="w-4 h-4" />
                    </Button>
                  {/if}
                </div>
              </div>

              <!-- Full Name -->
              <div class="space-y-2">
                <label for="full-name" class="form-label">
                  {$_('profile.fields.full_name')} *
                </label>
                <input
                  id="full-name"
                  type="text"
                  bind:value={fullName}
                  placeholder={$_('profile.fields.full_name_placeholder')}
                  class="form-input"
                />
              </div>

              <!-- Display Name -->
              <div class="space-y-2">
                <label for="display-name" class="form-label">
                  {$_('profile.fields.nickname')}
                </label>
                <input
                  id="display-name"
                  type="text"
                  bind:value={displayName}
                  placeholder={$_('profile.fields.nickname_placeholder')}
                  class="form-input"
                />
                <p class="text-xs text-foreground/70">
                  {$_('profile.fields.nickname_hint')}
                </p>
              </div>

              <!-- Email (read-only) -->
              <div class="space-y-2">
                <label for="email" class="form-label">
                  {$_('profile.fields.email')}
                </label>
                <div class="relative">
                  <Mail class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/50 pointer-events-none" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    disabled
                    style="padding-left: 2.5rem"
                    class="form-input"
                  />
                </div>
                <p class="text-xs text-foreground/70">
                  {$_('profile.fields.email_hint')}
                </p>
              </div>

              <!-- Auth Method Info -->
              <div class="p-3 bg-foreground/10 rounded-lg">
                <div class="flex items-center gap-2 text-sm text-foreground">
                  {#if authMethod === 'google'}
                    <span>{$_('profile.auth.google')}</span>
                  {:else}
                    <Mail class="w-5 h-5" />
                    <span>{$_('profile.auth.email')}</span>
                  {/if}
                </div>
              </div>
            </div>
          </Card>

        {:else if activeTab === 'security'}
          <!-- Security Tab -->
          <Card class="widget-card tone-header">
            <div class="space-y-4">
              <p class="text-sm text-foreground/70 mb-4">
                {$_('profile.security.subtitle')}
              </p>

              <!-- New Password -->
              <div class="space-y-2">
                <label for="new-password" class="form-label">
                  {$_('profile.security.new_password')}
                </label>
                <div class="relative">
                  <Key class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/50" />
                  <input
                    id="new-password"
                    type="password"
                    bind:value={newPassword}
                    placeholder="••••••••"
                    class="form-input pl-10"
                  />
                </div>

                {#if newPassword.length > 0}
                  <div class="space-y-1">
                    <div class="flex gap-1">
                      <div class="h-1 flex-1 rounded-full {passwordStrength === 'weak' ? 'bg-destructive' : passwordStrength === 'medium' ? 'bg-warning' : 'bg-primary'}"></div>
                      <div class="h-1 flex-1 rounded-full {passwordStrength === 'medium' || passwordStrength === 'strong' ? passwordStrength === 'medium' ? 'bg-warning' : 'bg-primary' : 'bg-foreground/20'}"></div>
                      <div class="h-1 flex-1 rounded-full {passwordStrength === 'strong' ? 'bg-primary' : 'bg-foreground/20'}"></div>
                    </div>
                    <p class="text-xs {passwordStrength === 'weak' ? 'text-destructive' : passwordStrength === 'medium' ? 'text-warning' : 'text-primary'}">
                      {passwordStrength === 'weak' ? $_('profile.security.password_strength.weak') : passwordStrength === 'medium' ? $_('profile.security.password_strength.medium') : $_('profile.security.password_strength.strong')}
                    </p>
                  </div>
                {/if}
              </div>

              <!-- Confirm Password -->
              <div class="space-y-2">
                <label for="confirm-password" class="form-label">
                  {$_('profile.security.confirm_password')}
                </label>
                <div class="relative">
                  <Key class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/50" />
                  <input
                    id="confirm-password"
                    type="password"
                    bind:value={confirmPassword}
                    placeholder="••••••••"
                    class="form-input pl-10"
                  />
                </div>
                {#if confirmPassword.length > 0 && newPassword !== confirmPassword}
                  <p class="text-xs text-destructive">{$_('profile.security.password_mismatch')}</p>
                {/if}
              </div>

              <Button
                onclick={handleChangePassword}
                class="w-full"
                disabled={!isPasswordValid || isSaving}
              >
                <Key class="w-4 h-4 mr-2" />
                {isSaving ? $_('profile.security.changing_password') : $_('profile.security.change_password')}
              </Button>

              <p class="text-xs text-foreground/70">
                {$_('profile.security.password_hint')}
              </p>
            </div>
          </Card>
        {/if}
      </div>
    </div>

    <!-- Footer -->
    <div class="p-6 border-t border-border flex gap-3">
      <Button variant="outline" onclick={handleCancel} class="btn btn-secondary flex-1">
        {$_('profile.save.cancel')}
      </Button>
      {#if activeTab === 'profile'}
        <Button
          onclick={handleSaveProfile}
          class="btn btn-primary flex-1"
          disabled={!isFormValid || isSaving}
        >
          <Save class="w-4 h-4 mr-2" />
          {isSaving ? $_('profile.save.saving') : $_('profile.save.save')}
        </Button>
      {/if}
    </div>
  </div>
{/if}
